import React, {useEffect, useState} from 'react';
import {
	Layout,
	Card,
	Typography,
	Button,
	Tag,
	Spin,
	message,
	Empty,
} from 'antd';
import {BookOutlined, LoadingOutlined} from '@ant-design/icons';
import AppHeader from '../../../components/Dashboard/AppHeader';
import {enrollCourseAPI, getCoursesForStudentsAPI} from '../../../zustand/API';
import userStore from '../../../zustand/store/userStore';

const {Content} = Layout;
const {Title} = Typography;

const CourseDetails = () => {
	const [subjects, setSubjects] = useState([]);
	const [loading, setLoading] = useState(true);
	const {user} = userStore();

	const fetchCourses = async () => {
		setLoading(true);
		try {
			const data = await getCoursesForStudentsAPI({
				className: user?.grade,
				email: user?.email,
			});
			if (Array.isArray(data?.subjects)) {
				setSubjects(data.subjects);
			} else {
				setSubjects([]);
			}
		} catch (err) {
			console.error('Failed to fetch courses:', err);
			message.error('Failed to refresh course list')
			setSubjects([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchCourses();
	}, []);

	const handleClick = async (subject) => {
		try {
			const response = await enrollCourseAPI({
				email: user?.email,
				parentEmail: user?.parentEmail,
				teacherEmail: subject?.teacherEmail,
				className: user?.grade,
				subject: subject?.name,
				teacherName: subject?.teacherName,
			});

			if (response?.status === 200) {
				message.success(
					response?.data?.message || 'Course enrolled successfully!',
				);
				await fetchCourses();
			}
		} catch (error) {
			console.error('error', error);
			message.error(error?.response?.data?.message || 'Failed to enroll');
		}
	};

	return (
		<Content className="p-2 min-h-screen">
			<AppHeader
				title={'View Course Details'}
				content={
					'Purpose: To allow students to see the list of courses they are enrolled in and access course details.'
				}
			/>

			<Title level={4} className="mb-4">
				📚 All Courses
			</Title>

			{loading ? (
				<div className="flex justify-center items-center h-64">
					<Spin
						indicator={
							<LoadingOutlined style={{fontSize: 36, color: '#22C55E'}} spin />
						}
						tip="Loading courses..."
					/>
				</div>
			) : subjects?.length === 0 ? (
				<Empty description="No courses found." className="mt-10" />
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					{subjects?.map((subject, index) => (
						<Card
							key={index}
							className="rounded-2xl shadow-md hover:shadow-xl transition duration-300 border border-gray-200"
							bodyStyle={{padding: '20px'}}
						>
							<div className="flex items-center gap-3 mb-3">
								<BookOutlined className="text-xl text-[#3B82F6]" />
								<Title level={5} className="!mb-0">
									{subject.name}
								</Title>
							</div>

							<Tag
								color="blue"
								className="rounded-full px-3 py-1 mb-2 inline-block"
							>
								{subject.lectureCount} Lecture
								{subject.lectureCount > 1 ? 's' : ''}
							</Tag>

							<div className="mb-3">
								<p className="text-sm text-gray-600">
									<strong>👨‍🏫 Teacher:</strong> {subject.teacherName}
								</p>
								<p className="text-sm text-gray-600">
									<strong>📧 Email:</strong> {subject.teacherEmail}
								</p>
							</div>

							<Button
								type="primary"
								className="bg-[#22C55E] w-full rounded-xl font-semibold"
								onClick={() => handleClick(subject)}
							>
								Enroll Course
							</Button>
						</Card>
					))}
				</div>
			)}
		</Content>
	);
};

export default CourseDetails;
